// The Unfathomable page is private when the server has a password: nothing else (rooms, game,
// art) loads until the browser has passed this gate. The server sets an HttpOnly cookie.
import { useEffect, useState, type ReactNode } from "react";

import css from "./Unfathomable.module.css";
import { gameHttpBase } from "./useUnfathomableRoom";

type GateState = "checking" | "open" | "locked";

export function PasswordGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>("checking");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${gameHttpBase()}/auth`, { credentials: "include", cache: "no-store" })
      .then(r => r.json())
      .then((s: { required: boolean; ok: boolean }) => setState(!s.required || s.ok ? "open" : "locked"))
      // Server unreachable: let the normal page show its own connection errors.
      .catch(() => setState("open"));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${gameHttpBase()}/auth`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }),
      });
      const body = await r.json().catch(() => ({}));
      if (r.ok && body.ok) {
        // Reload so the art pack and the game connection start fresh with the cookie.
        window.location.reload();
        return;
      }
      setError(body.error ?? "无法登录");
    } catch {
      setError("无法连接服务器");
    }
    setBusy(false);
  };

  if (state === "open") return <>{children}</>;
  if (state === "checking") return <div className={css.gateWrap} />;
  return (
    <div className={css.gateWrap}>
      <form className={`${css.gateCard} ${css.frame}`} onSubmit={submit}>
        <h1 className={css.title} style={{ margin: 0, color: "var(--brass-lo)", textShadow: "none" }}>Unfathomable</h1>
        <div className={css.subtitle} style={{ color: "var(--brass-lo)" }}>深 渊 之 中</div>
        <div className={css.rule} />
        <p style={{ margin: "6px 0 12px" }}>这艘船只接待受邀的乘客。请出示通行口令。</p>
        <input className={css.input} type="password" autoFocus autoComplete="current-password" value={password}
          onChange={e => setPassword(e.target.value)} placeholder="口令" style={{ width: "100%", boxSizing: "border-box" }} />
        {error && <div className={css.gateError}>{error}</div>}
        <button className={css.btn} type="submit" disabled={busy || !password} style={{ marginTop: 12, width: "100%", textAlign: "center" }}>
          {busy ? "核对中…" : "登船"}
        </button>
      </form>
    </div>
  );
}
